import type { CellContext } from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import useSWR from "swr";
import { useAuthContext } from "../../../../../components/Auth";
import {
  IconButtonMedium,
  PrimaryButtonWithPlusIcon,
} from "../../../../../components/Buttons/Buttons";
import { createChangePerPageHandler } from "../../../../../components/Pagination/util";
import type {
  ProductStatusType,
  AttributeSchemaBase,
} from "../../../../../types/types.PIM";
import {
  StatusBadge,
  getProductStatusColor,
  getProductStatusText,
} from "../../../../../components/Status/Status";
import { endpoints } from "../../../../../endpoints";
import type {
  EntityProfileSummary,
  EntityProfilePaginatedOutput,
  EntityType,
} from "../../../../../types/types.MDM";
import { ENTITY_MODIFY_PERMISSIONS } from "../../../../../types/types.MDM";
import type { WithPagination } from "../../../../../types/types";
import { useDebounce } from "../../../../../util/hooks";
import {
  defaultHandleSort,
  formatDate,
  rowHover,
  TablePlaceholder,
  updateCustomLabels,
  useMediaQueries,
  useStoreState,
  useSupportedLanguages,
} from "../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import { TableCellTwoLine } from "../../../../../components/Table/TableCellTwoLine";
import ReactTooltip from "react-tooltip";
import { ENTITY_PERMISSIONS } from "./utils";
import {
  Breadcrumb,
  ButtonGroup,
  ContentContainer,
  FilterButtonContainer,
  FullwidthPageWrapper,
  MainContentWrapper,
  RightSideControls,
  SearchAndFiltersRow,
  SearchContainer,
  SelectedFiltersWrapper,
  StickyFiltersAndTotalWrapper,
  StickyFiltersContainer,
  StickyHeaderContainer,
} from "../../../../../components/ProductsLayout/SharedProductsLayout";
import { FiltersIconV2 } from "../../../../../components/Icons/Icons";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { WithPermission } from "../../../../../components/WithPermission/WithPermission";
import { FiltersFlyBar } from "../../../../../components/Filters/FiltersFlyBar";
import { SelectedFiltersArea } from "../../../../../components/Filters/SelectedFiltersArea";
import { Pagination } from "../../../../../components/Pagination/Pagination";
import { TagClassificationConfig } from "../../../../../types/types";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useStorageState } from "react-storage-hooks";
import { SortingRule } from "react-table";
import styled from "styled-components/macro";
import { TableSearchWrapper } from "../../SellerAdminSettings/PortfolioControlsTab/PorfolioControlsTab";
import { DropdownContainer } from "../../../../../components/Layout/Layout";
import { DropDown } from "../../../../../components/DropDown/DropDown";
import { Table } from "../../../../../components/Table/Table";
import {
  applyFiltersToURL,
  loadFilters,
} from "../../../../../util/filterStateManager";
import { TableContainer } from "../../../../SharedPages/ProductDetailPage/Assets/util";
import { ScrollTableWrapper } from "../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { useFilterPresets } from "../../../../../hooks/useFilterPresets";

const SearchContainerWithMaxWidth = styled(SearchContainer)`
  max-width: 450px;
`;
const TotalEntities = styled.div`
  color: ${({ theme }) => theme.resolve("color_neutral_gray500")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  margin: 0 0 ${({ theme }) => theme.resolve("spacing_md")};
  height: 15px;
`;

type EntityTableRow = {
  id: string;
  entity_number: string | null;
  company_name: string;
  erp_id: string | null;
  duns_number: string | null;
  parent_entity_name: string | null;
  subsidiaries_count: number | null;
  location: string | null;
  status: string;
  modified_at: string;
  modified_by: string;
  completion_score: {
    total: number;
    mandatory: number;
    show_total: boolean;
    show_mandatory: boolean;
  };
};

const ENTITY_ID_OBJECT_TYPES: Record<EntityType, string> = {
  customer: "customer_id",
  supplier: "supplier_id",
  distributor: "distributor_id",
};

const ENTITY_ID_FALLBACK_LABELS: Record<EntityType, string> = {
  customer: "Customer ID",
  supplier: "Supplier ID",
  distributor: "Distributor ID",
};

export function getEntityEndpoint(
  entityType: EntityType,
  tenantIdOrSlug: string
): string {
  switch (entityType) {
    case "customer":
      return endpoints.v2_tenants_id_customers(tenantIdOrSlug);
    case "supplier":
      return endpoints.v2_tenants_id_suppliers(tenantIdOrSlug);
    case "distributor":
      return endpoints.v2_tenants_id_distributors(tenantIdOrSlug);
  }
}

type EntityListPageProps = {
  entityType: EntityType;
  detailBasePath: string;
};

export function EntityListPage({
  entityType,
  detailBasePath,
}: EntityListPageProps): JSX.Element {
  const { t } = useTranslation();
  const {
    tenant_id,
    storefront_metadata: { only_bulk_upload_enabled },
  } = useStoreState();
  const { hasPermission } = useAuthContext();
  const { getLabel } = useMdmEntityDisplayNames();
  const pageTitle = getLabel(entityType, "plural");
  const entityLabelSingular = getLabel(entityType, "singular");
  const entityLabelPlural = pageTitle;
  const hasModifyOrPublishPermission =
    hasPermission(ENTITY_PERMISSIONS[entityType].modify) ||
    hasPermission(ENTITY_PERMISSIONS[entityType].publish);
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [offset, setOffset] = useState(0);

  const { roleIsSomeKindOfSeller } = useAuthContext();
  const location = useLocation();
  const pathname = location.pathname;
  const { isLargeScreen } = useMediaQueries();
  const search_ref = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const [filtersFromUrl, setFiltersFromUrl] =
    useState<{ [key: string]: string[] }>();
  const [tableData, setTableData] = useState<EntityTableRow[]>([]);
  const [perPage, setPerPage] = useState(query.perPage ?? 20);

  const perPageItems = [10, 20, 50];
  const [showFilters, setShowFilters] = useStorageState(
    sessionStorage,
    `${entityType}ListShowFilters`,
    isLargeScreen ? false : true
  );
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
    totalCount: 0,
  });
  const [tableTotalCount, setTableTotalCount] = useState(0);

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );
  const { supportedLanguageOptions } = useSupportedLanguages();
  const { storefront_id } = useStoreState();
  const endpoint = getEntityEndpoint(entityType, tenant_id);

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    sortBy: "modified_at",
    orderBy: "desc",
  });

  const { data, error, isValidating } = useSWR<EntityProfilePaginatedOutput>(
    [
      endpoint +
        "?" +
        applyFiltersToURL({
          filtersFromUrl,
          params: new URLSearchParams(
            `offset=${offset}&limit=${perPage}&order_by=${sortingRules.orderBy}&sort_by=${sortingRules.sortBy}`
          ),
        }),
    ],
    { revalidateOnFocus: false }
  );

  const { data: customLabels, error: customLabelsError } = useSWR<
    TagClassificationConfig[]
  >(
    storefront_id
      ? endpoints.v2_storefronts_id_mdm_entity_type_filters_custom_labels(
          storefront_id,
          entityType
        )
      : null,
    { revalidateOnFocus: true }
  );

  const {
    presetFilterData,
    mutatePresetFilter,
    selectedPreset,
    handleActivePresetId,
    clearSelectedPreset,
  } = useFilterPresets({
    storefrontId: storefront_id,
    entityType,
    canFetch: !!storefront_id,
    location,
    history,
    pathname,
    queryQ: query.q,
    queryOffset: query.offset,
    queryPerPage: query.perPage,
  });

  updateCustomLabels(customLabels, supportedLanguageOptions);

  const handleClearFilter = async (preventPresetActivation?: boolean) => {
    await clearSelectedPreset(preventPresetActivation);
    const destination = pathname;
    history.replace(destination);
    setQuery({ q: query.q, offset: query.offset, perPage: query.perPage });
  };

  const handleShowFilters = (showFiltersParam: boolean) => {
    setShowFilters(showFiltersParam);
  };

  const handleSort = async (rules: SortingRule<object>[]) => {
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);
  };

  useEffect(() => {
    if (data?.data) {
      setTableData(
        data.data.map((entity: EntityProfileSummary) => ({
          id: entity.id,
          entity_number: entity.entity_number,
          company_name: entity.name,
          erp_id: entity.erp_id,
          duns_number: entity.duns_number,
          parent_entity_name: entity.parent_entity_name,
          subsidiaries_count: entity.subsidiaries_count,
          location: entity.location,
          status: entity.status,
          modified_at: entity.modified_at,
          modified_by: entity.modified_by ?? "",
          completion_score: {
            total: entity.completion_score ?? 0,
            mandatory: entity.mandatory_completion_score ?? 0,
            show_total: entity.show_total_completion_score ?? false,
            show_mandatory: entity.show_mandatory_completion_score ?? false,
          },
          rowURL: `${detailBasePath}/${entity.id}`,
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(data.pagination.total / perPage),
        pageIndex: Math.floor(offset / perPage) + 1,
        totalCount: data.pagination.total,
      });
      setTableTotalCount(data.pagination.total);
    }
  }, [data, detailBasePath, perPage, offset]);

  const { data: attributesList } = useSWR<
    WithPagination<{ data: AttributeSchemaBase[] }>,
    Error
  >(
    storefront_id
      ? `${endpoints.v2_storefronts_id_pim_attributes(
          storefront_id
        )}?object_type=${ENTITY_ID_OBJECT_TYPES[entityType]}&limit=10`
      : null
  );

  const entityIdLabel =
    attributesList?.data?.find(
      (attribute) =>
        attribute.object_type === ENTITY_ID_OBJECT_TYPES[entityType] ||
        attribute.name === ENTITY_ID_OBJECT_TYPES[entityType]
    )?.display_name || t(ENTITY_ID_FALLBACK_LABELS[entityType]);

  // Hide DUNS column if no profiles have a DUNS number
  const hasDunsData = useMemo(() => {
    if (!data?.data) return false;
    return data.data.some(
      (entity: EntityProfileSummary) => entity.duns_number != null
    );
  }, [data]);

  const columns = useMemo(() => {
    const allColumns = [
      {
        header: entityIdLabel,
        accessorKey: "entity_number",
        id: "entity_number",
        cell: ({ getValue }: CellContext<EntityTableRow, string | null>) => (
          <>{getValue() || "—"}</>
        ),
      },
      {
        header: t("Company Name"),
        accessorKey: "company_name",
        id: "company_name",
      },
      {
        header: t("ERP ID"),
        accessorKey: "erp_id",
        id: "erp_id",
        cell: ({ getValue }: CellContext<EntityTableRow, string | null>) => (
          <>{getValue() || "—"}</>
        ),
      },
      ...(hasDunsData
        ? [
            {
              header: t("DUNS Number"),
              accessorKey: "duns_number" as const,
              id: "duns_number",
              cell: ({
                getValue,
              }: CellContext<EntityTableRow, string | null>) => (
                <>{getValue() || "—"}</>
              ),
            },
          ]
        : []),
      {
        header: t("Parent Entity"),
        accessorKey: "parent_entity_name",
        id: "parent_entity_name",
        cell: ({ getValue }: CellContext<EntityTableRow, string | null>) => (
          <>{getValue() || "—"}</>
        ),
      },
      {
        header: t("Subsidiaries"),
        accessorKey: "subsidiaries_count",
        id: "subsidiaries_count",
        cell: ({ getValue }: CellContext<EntityTableRow, number | null>) => {
          const count = getValue();
          return <>{count != null ? count : "—"}</>;
        },
      },
      {
        header: t("Location"),
        accessorKey: "location",
        id: "location",
        cell: ({ getValue }: CellContext<EntityTableRow, string | null>) => (
          <>{getValue() || "—"}</>
        ),
      },
      {
        header: t("Last Modified By"),
        accessorKey: "modified_by",
        id: "modified_by",
        cell: ({ row }: CellContext<EntityTableRow, string>) => {
          const modifiedAt = row.original.modified_at;
          const modifiedBy = row.original.modified_by;
          return (
            <TableCellTwoLine
              primaryText={modifiedBy}
              secondaryText={modifiedAt ? formatDate(modifiedAt) : undefined}
            />
          );
        },
      },
      {
        header: t("Completion"),
        accessorKey: "completion_score",
        id: "completion_score",
        cell: ({
          getValue,
        }: CellContext<
          EntityTableRow,
          {
            total: number;
            mandatory: number | null;
            show_total: boolean;
            show_mandatory: boolean;
          } | null
        >) => {
          const completionScore = getValue();

          // Add defensive check for undefined/null value
          if (!completionScore) {
            return <span>-</span>;
          }

          if (
            !completionScore?.show_total &&
            !completionScore?.show_mandatory
          ) {
            return <span>--</span>;
          }

          const totalScore = completionScore?.show_total
            ? completionScore?.total
            : null;
          const mandatoryScore = completionScore?.show_mandatory
            ? completionScore?.mandatory
            : null;

          // If only total score should be shown
          if (totalScore !== null && mandatoryScore === null) {
            return (
              <>
                <div
                  data-tip={t("Total Completion: {{total}}", {
                    total: totalScore,
                  })}
                  data-for="completion-tooltip"
                >
                  {`${totalScore.toString().padStart(4, " ")}%`}
                </div>
                <ReactTooltip id="completion-tooltip" effect="solid" />
              </>
            );
          }

          // If only mandatory score should be shown
          if (totalScore === null && mandatoryScore !== null) {
            return (
              <>
                <div
                  data-tip={t("Mandatory Completion: {{mandatory}}", {
                    mandatory: mandatoryScore,
                  })}
                  data-for="completion-tooltip"
                >
                  {`${mandatoryScore.toString().padStart(4, " ")}%`}
                </div>
                <ReactTooltip id="completion-tooltip" effect="solid" />
              </>
            );
          }

          // Both scores are shown
          if (totalScore !== null && mandatoryScore !== null) {
            return (
              <>
                <div
                  data-tip={t(
                    "Total Completion: {{total}} | Mandatory Completion: {{mandatory}}",
                    {
                      total: totalScore,
                      mandatory: mandatoryScore,
                    }
                  )}
                  data-for="completion-tooltip"
                >
                  {`${totalScore
                    .toString()
                    .padStart(4, " ")}% | ${mandatoryScore
                    .toString()
                    .padStart(4, " ")}%`}
                </div>
                <ReactTooltip id="completion-tooltip" effect="solid" />
              </>
            );
          }

          // Fallback for unexpected state
          return <span>-</span>;
        },
      },
      ...(hasModifyOrPublishPermission
        ? [
            {
              header: t("Status"),
              accessorKey: "status" as const,
              id: "status",
              cell: ({ getValue }: CellContext<EntityTableRow, string>) => {
                const status = getValue();
                if (!status) return <>—</>;
                const color = getProductStatusColor(
                  status as ProductStatusType
                );
                const label = getProductStatusText(
                  status as ProductStatusType,
                  t
                );
                return <StatusBadge text={label} color={color} />;
              },
            },
          ]
        : []),
    ];

    return allColumns;
  }, [entityIdLabel, hasDunsData, hasModifyOrPublishPermission, t]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setOffset(0);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
    setQuery({ q: undefined, offset: 0, perPage: perPage });
  };

  useEffect(() => {
    const filters = loadFilters(location);
    setFiltersFromUrl(filters);
  }, [location]);

  useEffect(() => {
    setQuery({
      q: debouncedSearchQuery.length > 0 ? debouncedSearchQuery : undefined,
    });
  }, [debouncedSearchQuery, setQuery]);

  useEffect(() => {
    sessionStorage.setItem(`${entityType}_search`, location.search);
  }, [location.search, entityType]);

  const isLoading = isValidating && !data;

  return (
    <FullwidthPageWrapper>
      <StickyHeaderContainer>
        <Breadcrumb>
          <div>{pageTitle}</div>
        </Breadcrumb>
        <SearchAndFiltersRow>
          {customLabels && (
            <FilterButtonContainer>
              <IconButtonMedium
                onClick={() => handleShowFilters(!showFilters)}
                aria-label={t("Filters")}
              >
                <FiltersIconV2 />
              </IconButtonMedium>
            </FilterButtonContainer>
          )}
          <SearchContainerWithMaxWidth>
            <SearchBar
              query={searchQuery}
              placeHolder={t("Search by name or ID...")}
              handleChange={handleSearch}
              handleClearInput={handleClearSearch}
              ref={search_ref}
            />
          </SearchContainerWithMaxWidth>
          <RightSideControls>
            <ButtonGroup>
              <WithPermission
                permission={[
                  ENTITY_MODIFY_PERMISSIONS[entityType],
                  "view_templates",
                ]}
                permission_type="every"
              >
                <PrimaryButtonWithPlusIcon
                  onClick={() => history.push(`${detailBasePath}/new`)}
                  useV2
                >
                  {only_bulk_upload_enabled ? t("Upload") : t("Create")}
                </PrimaryButtonWithPlusIcon>
              </WithPermission>
            </ButtonGroup>
          </RightSideControls>
        </SearchAndFiltersRow>
      </StickyHeaderContainer>
      <ContentContainer showFilters={showFilters}>
        <StickyFiltersContainer show={showFilters}>
          <FiltersFlyBar
            customLabels={customLabels ?? []}
            handleShowFilters={handleShowFilters}
            show={showFilters}
            selectedPreset={selectedPreset}
            presetFilterData={presetFilterData}
            mutatePresetFilter={mutatePresetFilter}
            handleActivePresetId={handleActivePresetId}
            clearFilter={handleClearFilter}
            resetOffset={setOffset}
            entityType={entityType}
          />
        </StickyFiltersContainer>
        <MainContentWrapper showFilters={showFilters}>
          <StickyFiltersAndTotalWrapper>
            {!customLabelsError && (
              <SelectedFiltersWrapper
                hasFilters={(customLabels && customLabels.length > 0) ?? false}
              >
                <SelectedFiltersArea
                  entityType={entityType}
                  clearFilter={handleClearFilter}
                  customLabels={customLabels ?? []}
                  filtersList={[
                    ...(customLabels?.map(
                      (filter: TagClassificationConfig) => filter.filter_type
                    ) || []),
                    // Add status and template filters for seller_admin
                    ...(roleIsSomeKindOfSeller ? ["status", "template"] : []),
                  ]}
                  isPublic={false}
                />
              </SelectedFiltersWrapper>
            )}
          </StickyFiltersAndTotalWrapper>
          <TableSearchWrapper style={{ padding: "3px 16px", flex: "unset" }}>
            <DropdownContainer>
              <DropDown
                items={perPageItems}
                activeItem={perPage}
                textLeft={t("items") + ":"}
                textRight={t(" Per Page")}
                direction={"right"}
                className={"per_Page"}
                clickHandler={changePerPage}
              ></DropDown>
              {tableTotalCount > 0 && (
                <TotalEntities style={{ marginBottom: "0" }}>
                  {tableTotalCount}{" "}
                  {tableTotalCount === 1
                    ? entityLabelSingular
                    : entityLabelPlural}
                </TotalEntities>
              )}
            </DropdownContainer>
          </TableSearchWrapper>
          <TableContainer>
            <ScrollTableWrapper maxHeight={`calc(100vh - 350px)`}>
              <Table
                columns={columns}
                data={tableData}
                isLoading={isLoading}
                error={error}
                rowHover={rowHover}
                handleSort={handleSort}
                tableHeight="100%"
                Placeholder={
                  <TablePlaceholder
                    message={
                      searchQuery.length > 0
                        ? t(`No results found.`)
                        : t(`No {{entityType}} found.`, {
                            entityType: entityLabelPlural,
                          })
                    }
                  />
                }
              />
            </ScrollTableWrapper>
          </TableContainer>
          <Pagination
            key={"table"}
            pagination={tablePagination}
            offset={offset}
            handlePageClick={setOffset}
          />
        </MainContentWrapper>
      </ContentContainer>
    </FullwidthPageWrapper>
  );
}
